import {
    ACTING_COMPANY_HEADER,
    actingCompanyHeaderFor,
} from './supportSession.js';

export class ApiV2Error extends Error {
    constructor({ status, code, message, details = {}, requestId = null }) {
        super(message);
        this.name = 'ApiV2Error';
        this.status = status;
        this.code = code;
        this.details = details;
        this.requestId = requestId;
    }
}

function buildApiV2Url(apiRoot, path, query = null) {
    if (typeof path !== 'string' || !path.startsWith('/')) {
        throw new TypeError('API v2 paths must start with /.');
    }
    if (path === '/v2' || path.startsWith('/v2/')) {
        throw new TypeError('API v2 paths must not include the /v2 prefix.');
    }

    const normalizedRoot = String(apiRoot || '').replace(/\/+$/, '');
    if (!normalizedRoot) {
        throw new TypeError('An API root is required.');
    }

    const url = new URL(`${normalizedRoot}/v2${path}`);
    if (query) {
        Object.entries(query).forEach(([key, value]) => {
            if (value === undefined || value === null || value === '') return;
            if (Array.isArray(value)) {
                value.forEach((entry) => url.searchParams.append(key, entry));
                return;
            }
            url.searchParams.set(key, value);
        });
    }
    return url.toString();
}

function requestSignal(signal, timeoutMs) {
    if (timeoutMs === undefined || timeoutMs === null) {
        return { signal, timedOut: () => false, cleanup() {} };
    }
    if (!Number.isSafeInteger(timeoutMs) || timeoutMs < 1) {
        throw new TypeError('timeoutMs must be a positive integer.');
    }

    const controller = new AbortController();
    let didTimeOut = false;
    const abortFromCaller = () => controller.abort(signal?.reason);
    if (signal?.aborted) abortFromCaller();
    else signal?.addEventListener('abort', abortFromCaller, { once: true });
    const timer = setTimeout(() => {
        didTimeOut = true;
        controller.abort();
    }, timeoutMs);

    return {
        signal: controller.signal,
        timedOut: () => didTimeOut,
        cleanup() {
            clearTimeout(timer);
            signal?.removeEventListener('abort', abortFromCaller);
        },
    };
}

async function readResponseBody(response, requireValidJson = false) {
    if (response.status === 204 || response.status === 205) return null;

    const text = await response.text();
    if (!text.trim()) {
        if (!requireValidJson) return null;
        throw new ApiV2Error({
            status: response.status,
            code: 'INVALID_API_RESPONSE',
            message: 'The server returned an invalid API response.',
        });
    }

    try {
        return JSON.parse(text);
    } catch {
        if (!requireValidJson) return null;
        throw new ApiV2Error({
            status: response.status,
            code: 'INVALID_API_RESPONSE',
            message: 'The server returned an invalid API response.',
        });
    }
}

function setActingCompanyHeader(headers, path) {
    const actingCompanyId = actingCompanyHeaderFor(path);
    if (actingCompanyId) headers.set(ACTING_COMPANY_HEADER, actingCompanyId);
}

function createJsonHeaders(body, path) {
    const headers = new Headers({ Accept: 'text/event-stream' });
    if (body !== undefined) headers.set('Content-Type', 'application/json');
    setActingCompanyHeader(headers, path);
    return headers;
}

function parseSseEvent(block) {
    let event = 'message';
    const data = [];
    block.split(/\r\n|\r|\n/).forEach((line) => {
        if (!line || line.startsWith(':')) return;
        const separator = line.indexOf(':');
        const field = separator < 0 ? line : line.slice(0, separator);
        let value = separator < 0 ? '' : line.slice(separator + 1);
        if (value.startsWith(' ')) value = value.slice(1);
        if (field === 'event') event = value;
        if (field === 'data') data.push(value);
    });
    if (!data.length) return null;
    return { event, data: data.join('\n') };
}

async function consumeSseBody(body, onEvent) {
    if (!body?.getReader) {
        throw new ApiV2Error({
            status: 200,
            code: 'INVALID_API_RESPONSE',
            message: 'The server returned an invalid streaming response.',
        });
    }
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    const dispatch = (block) => {
        const parsed = parseSseEvent(block);
        if (!parsed) return;
        if (parsed.event === 'error') {
            let payload;
            try {
                payload = JSON.parse(parsed.data);
            } catch {
                payload = null;
            }
            const error = payload?.error;
            throw new ApiV2Error({
                status: 200,
                code: error?.code || 'AUDIT_AGENT_STREAM_FAILED',
                message:
                    error?.message || 'Patty could not complete the response.',
                details: error?.details || {},
                requestId: error?.requestId || null,
            });
        }
        onEvent(parsed);
    };

    let finished = false;
    while (!finished) {
        const { done, value } = await reader.read();
        buffer += decoder.decode(value, { stream: !done });
        let boundary = buffer.match(/\r\n\r\n|\n\n|\r\r/);
        while (boundary?.index !== undefined) {
            dispatch(buffer.slice(0, boundary.index));
            buffer = buffer.slice(boundary.index + boundary[0].length);
            boundary = buffer.match(/\r\n\r\n|\n\n|\r\r/);
        }
        finished = done;
    }
    if (buffer.trim()) dispatch(buffer);
}

export function createApiV2Client({
    apiRoot,
    fetchImpl = globalThis.fetch,
} = {}) {
    if (typeof fetchImpl !== 'function') {
        throw new TypeError('A fetch implementation is required.');
    }

    return {
        async stream(
            path,
            {
                method = 'POST',
                body,
                signal,
                timeoutMs,
                onEvent = () => {},
            } = {}
        ) {
            const deadline = requestSignal(signal, timeoutMs);
            try {
                const response = await fetchImpl(buildApiV2Url(apiRoot, path), {
                    method,
                    credentials: 'include',
                    headers: createJsonHeaders(body, path),
                    signal: deadline.signal,
                    ...(body === undefined
                        ? {}
                        : { body: JSON.stringify(body) }),
                });
                if (!response.ok) {
                    const responseBody = await readResponseBody(response);
                    const apiError = responseBody?.error;
                    throw new ApiV2Error({
                        status: response.status,
                        code: apiError?.code || 'API_REQUEST_FAILED',
                        message:
                            apiError?.message ||
                            `API request failed with status ${response.status}.`,
                        details: apiError?.details || {},
                        requestId:
                            apiError?.requestId ||
                            response.headers.get('X-Request-ID') ||
                            null,
                    });
                }
                if (
                    !response.headers
                        .get('Content-Type')
                        ?.toLowerCase()
                        .includes('text/event-stream')
                ) {
                    throw new ApiV2Error({
                        status: response.status,
                        code: 'INVALID_API_RESPONSE',
                        message:
                            'The server returned an invalid streaming response.',
                    });
                }
                await consumeSseBody(response.body, onEvent);
            } catch (error) {
                if (deadline.timedOut()) {
                    throw new ApiV2Error({
                        status: 0,
                        code: 'API_REQUEST_TIMEOUT',
                        message: 'The request took too long. Try again.',
                    });
                }
                throw error;
            } finally {
                deadline.cleanup();
            }
        },
        async request(
            path,
            {
                method = 'GET',
                query = null,
                body,
                ifMatch,
                idempotencyKey,
                signal,
                timeoutMs,
                responseType = 'json',
                includeResponseMeta = false,
            } = {}
        ) {
            if (!['json', 'blob'].includes(responseType)) {
                throw new TypeError('responseType must be json or blob.');
            }
            const headers = new Headers({
                Accept:
                    responseType === 'blob'
                        ? 'application/octet-stream'
                        : 'application/json',
            });
            const isFormData =
                typeof FormData !== 'undefined' && body instanceof FormData;
            if (body !== undefined && !isFormData) {
                headers.set('Content-Type', 'application/json');
            }
            if (ifMatch !== undefined && ifMatch !== null) {
                headers.set('If-Match', String(ifMatch));
            }
            if (idempotencyKey) {
                headers.set('Idempotency-Key', idempotencyKey);
            }
            setActingCompanyHeader(headers, path);

            const deadline = requestSignal(signal, timeoutMs);
            try {
                const response = await fetchImpl(
                    buildApiV2Url(apiRoot, path, query),
                    {
                        method,
                        credentials: 'include',
                        headers,
                        signal: deadline.signal,
                        ...(body === undefined
                            ? {}
                            : {
                                  body: isFormData
                                      ? body
                                      : JSON.stringify(body),
                              }),
                    }
                );
                if (response.ok && responseType === 'blob') {
                    const data = await response.blob();
                    return includeResponseMeta
                        ? {
                              data,
                              status: response.status,
                              etag: response.headers.get('ETag'),
                              contentDisposition: response.headers.get(
                                  'Content-Disposition'
                              ),
                              exportAuditId: response.headers.get(
                                  'X-ABX-Export-Audit-ID'
                              ),
                              idempotencyReplayed:
                                  response.headers.get(
                                      'Idempotency-Replayed'
                                  ) === 'true',
                          }
                        : data;
                }
                const responseBody = await readResponseBody(
                    response,
                    response.ok
                );

                if (response.ok) {
                    return includeResponseMeta
                        ? {
                              data: responseBody,
                              status: response.status,
                              etag: response.headers.get('ETag'),
                              idempotencyReplayed:
                                  response.headers.get(
                                      'Idempotency-Replayed'
                                  ) === 'true',
                          }
                        : responseBody;
                }

                const apiError = responseBody?.error;
                throw new ApiV2Error({
                    status: response.status,
                    code: apiError?.code || 'API_REQUEST_FAILED',
                    message:
                        apiError?.message ||
                        `API request failed with status ${response.status}.`,
                    details: apiError?.details || {},
                    requestId:
                        apiError?.requestId ||
                        response.headers.get('X-Request-ID') ||
                        null,
                });
            } catch (error) {
                if (deadline.timedOut()) {
                    throw new ApiV2Error({
                        status: 0,
                        code: 'API_REQUEST_TIMEOUT',
                        message: 'The request took too long. Try again.',
                    });
                }
                throw error;
            } finally {
                deadline.cleanup();
            }
        },
    };
}

export { buildApiV2Url, consumeSseBody };
